/*
  Stylo 88 — Movimiento y rendimiento en móvil
  ---------------------------------------------
  - No toca colores ni tipografías: solo opacity/transform y carga de imágenes.
  - Si este archivo falla o no carga, la web queda tal cual estaba (el CSS de
    movimiento depende de la clase .s88-motion que se agrega aquí).
  - Se desactiva solo si el usuario pide "reducir movimiento" o navega con
    ahorro de datos.
*/
(() => {
  const root = document.documentElement;
  if (root.classList.contains('s88-motion')) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = navigator.connection && navigator.connection.saveData === true;

  root.classList.add('s88-motion');

  /* ---------------- 1. Aparición al hacer scroll ---------------- */

  // Selectores de bloques que valen la pena animar en la home y en colección.
  const REVEAL_TARGETS = [
    '.rfc-title',
    '.rfc-wrapper > a',
    '.sg-item',
    '.s88env__card',
    '.st-card',
    '.sf-inner details',
    '.sp-card',
    '.sp-logos img',
    '.product-grid__item',
  ];

  const revealFor = (el) => {
    if (el.matches('.rfc-wrapper > a, .sg-item, .sp-logos img')) return 'scale';
    if (el.matches('.rfc-title')) return 'left';
    return '';
  };

  const setupReveals = () => {
    if (reduced || saveData || !('IntersectionObserver' in window)) return;

    const vh = window.innerHeight;
    const groups = new Map();

    document.querySelectorAll(REVEAL_TARGETS.join(',')).forEach((el) => {
      const rect = el.getBoundingClientRect();
      // Lo que ya se ve al cargar no se anima: evita el parpadeo inicial.
      if (rect.top < vh * 0.92) return;
      if (el.hasAttribute('data-s88-reveal')) return;

      el.setAttribute('data-s88-reveal', revealFor(el));

      // Escalona por grupo de hermanos (fila de tarjetas, chips, etc.)
      const parent = el.parentElement;
      const index = groups.get(parent) || 0;
      groups.set(parent, index + 1);
      if (index > 0) el.style.setProperty('--s88-delay', `${Math.min(index, 5) * 65}ms`);
    });

    // Red de seguridad: si por lo que sea el observer nunca llega a correr
    // (pestaña en segundo plano al cargar, navegador raro, error de JS), el
    // contenido NO puede quedarse invisible. A los 5 s se muestra todo.
    let observerCorrio = false;
    const mostrarTodo = () => {
      document.querySelectorAll('[data-s88-reveal]:not(.s88-in)').forEach((el) => {
        el.classList.add('s88-in', 's88-done');
        el.style.removeProperty('--s88-delay');
      });
    };
    setTimeout(() => {
      if (!observerCorrio) mostrarTodo();
    }, 5000);

    const observer = new IntersectionObserver(
      (entries) => {
        observerCorrio = true;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          el.classList.add('s88-in');
          observer.unobserve(el);
          el.addEventListener(
            'transitionend',
            () => {
              el.classList.add('s88-done');
              el.style.removeProperty('--s88-delay');
            },
            { once: true }
          );
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    document.querySelectorAll('[data-s88-reveal]').forEach((el) => observer.observe(el));
  };

  /* ---------------- 2. Feedback al tocar ---------------- */

  const TAP_TARGETS = [
    '.rfc-wrapper > a',
    '.rh__button',
    '.rh__dot',
    '.product-card',
    '.button',
    '.sf-inner summary',
    '.s88env__cta',
    '.st-instagram-button',
  ];

  const setupTaps = () => {
    document.querySelectorAll(TAP_TARGETS.join(',')).forEach((el) => el.classList.add('s88-tap'));
  };

  /* ---------------- 3. No pintar secciones fuera de pantalla ---------------- */

  const setupDeferredPaint = () => {
    if (!CSS.supports('content-visibility', 'auto')) return;
    const sections = [...document.querySelectorAll('#MainContent > .shopify-section')];
    // Las dos primeras siempre se pintan: son las que ve el usuario al entrar.
    sections.slice(2).forEach((s) => s.classList.add('s88-defer-paint'));
  };

  /* ---------------- 4. Hero: arrastrar con el dedo ---------------- */

  const setupHeroSwipe = () => {
    document.querySelectorAll('.rh').forEach((hero) => {
      const track = hero.querySelector('.rh__track');
      const prev = hero.querySelector('.rh__arrow--prev');
      const next = hero.querySelector('.rh__arrow--next');
      if (!track || !prev || !next || hero.dataset.s88Swipe === 'true') return;
      hero.dataset.s88Swipe = 'true';

      let startX = 0;
      let startY = 0;
      let dragging = false;
      let locked = null; // 'x' | 'y'

      const activeSlide = () => hero.querySelector('.rh__slide.is-active');

      track.addEventListener(
        'touchstart',
        (e) => {
          if (e.touches.length !== 1) return;
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
          dragging = true;
          locked = null;
        },
        { passive: true }
      );

      track.addEventListener(
        'touchmove',
        (e) => {
          if (!dragging) return;
          const dx = e.touches[0].clientX - startX;
          const dy = e.touches[0].clientY - startY;

          if (locked === null) {
            if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
            locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          }
          if (locked !== 'x') return;

          const slide = activeSlide();
          if (!slide || reduced) return;
          // Resistencia: el dedo mueve la diapositiva a un tercio, como iOS.
          slide.classList.add('s88-dragging');
          slide.style.transform = `translate3d(${dx / 3}px, 0, 0)`;
        },
        { passive: true }
      );

      const endDrag = (e) => {
        if (!dragging) return;
        dragging = false;
        const slide = activeSlide();
        if (slide) {
          slide.classList.remove('s88-dragging');
          slide.style.transform = '';
        }
        if (locked !== 'x') return;

        const endX = (e.changedTouches && e.changedTouches[0].clientX) || startX;
        const dx = endX - startX;
        if (Math.abs(dx) < 45) return;

        const goNext = dx < 0;
        (goNext ? next : prev).click();

        const incoming = activeSlide();
        if (incoming && !reduced) {
          const cls = goNext ? 's88-slide-in' : 's88-slide-in-back';
          incoming.classList.remove('s88-slide-in', 's88-slide-in-back');
          void incoming.offsetWidth; // reinicia la animación
          incoming.classList.add(cls);
          incoming.addEventListener(
            'animationend',
            () => incoming.classList.remove(cls),
            { once: true }
          );
        }
      };

      track.addEventListener('touchend', endDrag, { passive: true });
      track.addEventListener('touchcancel', endDrag, { passive: true });
    });
  };

  /* ---------------- Arranque ---------------- */

  const init = () => {
    setupTaps();
    setupHeroSwipe();

    // Si la página se abrió en segundo plano no hay ciclo de render: el
    // observer no correría y quedaría todo oculto. En ese caso se espera
    // a que la pestaña esté visible antes de preparar las animaciones.
    if (document.hidden) {
      document.addEventListener('visibilitychange', function alVerse() {
        if (document.hidden) return;
        document.removeEventListener('visibilitychange', alVerse);
        setupReveals();
        setupDeferredPaint();
      });
    } else {
      setupReveals();
      setupDeferredPaint();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Secciones cargadas por el editor de temas o por paginación.
  document.addEventListener('shopify:section:load', init);
})();
